import { privateClient } from "@/api/privateClient";
import { SUPER_ADMIN_ENDPOINTS } from "../../endpoint";
import type { Campus, RegisterCampusPayload, ApiCampus } from "../types";

export const listCampuses = async (organizationId: number): Promise<Campus[]> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.CAMPUSES.LIST_BY_ORG(organizationId));
  return data.campuses ?? data;
};

export const registerCampus = async (payload: RegisterCampusPayload): Promise<Campus> => {
  const { data } = await privateClient.post(SUPER_ADMIN_ENDPOINTS.CAMPUSES.REGISTER, payload);
  return data;
};

export const resetCampusAdminPassword = async (userId: number | string) => {
  const { data } = await privateClient.post(`/users/${userId}/reset-password`);
  return data;
};

export const updateCampus = async (
  id: number,
  payload: Record<string, unknown>
): Promise<{ campus: Campus }> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.CAMPUSES.UPDATE(String(id)), payload);
  return data;
};

// ---------------------------------------------------------------------
// Single-campus detail fetch, for CampusDetailsPage.tsx.
//
// GET /campuses/:id now returns everything needed directly — id, name,
// code, address fields, hasCampusAdminLogin, isActive, AND institutions
// (added server-side specifically for this — see
// backend/src/modules/campuses/services/campusService.js). This used to
// page through up to 100 organizations via the dashboard hierarchy
// endpoint to find one campus, because GET /campuses/:id genuinely didn't
// exist on the backend yet at the time this was written; it's since been
// added (and extended with the institutions array this page needs), so
// this fetches the campus directly and only makes a second, equally
// direct call for the parent organization's name/code (for the
// breadcrumb) — no more full-tree paging.
// ---------------------------------------------------------------------

export interface CampusDetails extends ApiCampus {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  organizationName: string;
  organizationCode: string;
  logoUrl?: string | null;
  bannerImage?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  backgroundColor?: string | null;
}

export async function getCampusDetails(
  organizationId: number,
  campusId: number
): Promise<CampusDetails> {
  const [campusRes, orgRes] = await Promise.all([
    privateClient.get(SUPER_ADMIN_ENDPOINTS.CAMPUSES.DETAIL(String(campusId))),
    privateClient.get(SUPER_ADMIN_ENDPOINTS.ORGANIZATIONS.DETAIL(String(organizationId))),
  ]);

  return {
    ...campusRes.data.campus,
    organizationName: orgRes.data.organization?.name ?? "",
    organizationCode: orgRes.data.organization?.code ?? "",
  };
}
